import {useEffect,useState} from "react";
import {collection,doc,getDoc,onSnapshot,runTransaction,serverTimestamp} from "firebase/firestore";
import {getStorage,ref,uploadBytes,getBlob} from "firebase/storage";
import {db} from "../firebaseConfig";
import {useAuth} from "../context/AuthContext";
import {getBannerInfo} from "../pages/bannerInfo";
import {appointmentBannerNotes} from "./appointmentBannerImport";
import {itemKey,STAGES,productionStage,designSelection,validDate,today,requireStage,previousDesignJobs} from "./bannerModel";

export function useBannerAccess() {
  const {currentUser,isSuperAdmin}=useAuth();
  const [grant,setGrant]=useState({uid:null,role:null,loading:true,error:""});
  useEffect(()=>{
    if(!currentUser || isSuperAdmin)return;
    return onSnapshot(doc(db,"banner_access",currentUser.uid),snapshot=>setGrant({uid:currentUser.uid,role:snapshot.data()?.role || null,loading:false,error:""}),()=>setGrant({uid:currentUser.uid,role:null,loading:false,error:"Could not check banner access. Refresh or contact an administrator."}));
  },[currentUser,isSuperAdmin]);
  const role=isSuperAdmin ? "manager" : grant.uid===currentUser?.uid ? grant.role : null;
  return {role,read:["viewer","designer","manager"].includes(role),edit:["designer","manager"].includes(role),bill:role==="manager",loading:!isSuperAdmin && Boolean(currentUser) && (grant.uid!==currentUser.uid || grant.loading),error:grant.error,isSuperAdmin,currentUser};
}
export function useBannerCollection(path,enabled=true) {
  const [state,setState]=useState({key:null,rows:[],error:"",loading:true});
  useEffect(()=>{
    if(!enabled)return;
    return onSnapshot(collection(db,...path.split("/")),snapshot=>setState({key:path,rows:snapshot.docs.map(record=>({...record.data(),id:record.id})),error:"",loading:false}),failure=>setState({key:path,rows:[],error:"Could not load banner data ("+failure.code+").",loading:false}));
  },[path,enabled]);
  if(!enabled)return {rows:[],error:"",loading:false};
  return state.key===path ? state : {rows:[],error:"",loading:true};
}
export async function importItems(jobId,items,uid) {
  if(items.length>300)throw new Error("Import at most 300 rows.");
  // The parent version serializes item imports and manual item edits across employees.
  const references=items.map(()=>doc(collection(db,"banner_jobs",jobId,"items")));
  return runTransaction(db,async transaction=>{
    const jobRef=doc(db,"banner_jobs",jobId),snapshot=await transaction.get(jobRef);
    if(!snapshot.exists())throw new Error("This job no longer exists.");
    const keys={...snapshot.data().itemKeys};
    let added=0;
    items.forEach((item,index)=>{
      const key=JSON.stringify(itemKey(item));
      if(Object.prototype.hasOwnProperty.call(keys,key))return;
      keys[key]=references[index].id;added++;
      transaction.set(references[index],{...item,createdBy:uid,createdAt:serverTimestamp(),updatedAt:serverTimestamp()});
    });
    if(Object.keys(keys).length>500)throw new Error("This version supports up to 500 banner items per team job.");
    const needsReview=added>0 && STAGES.indexOf(productionStage(snapshot.data().stage))>=3;
    transaction.update(jobRef,{itemKeys:keys,...(needsReview ? {stage:"Designing",approvalId:""} : {}),updatedAt:serverTimestamp()});
    return added;
  });
}
export async function saveItem(jobId,itemId,item) {
  return runTransaction(db,async transaction=>{
    const jobRef=doc(db,"banner_jobs",jobId),itemRef=doc(db,"banner_jobs",jobId,"items",itemId);
    const [job,previous]=await Promise.all([transaction.get(jobRef),transaction.get(itemRef)]);
    if(!job.exists() || !previous.exists())throw new Error("Job or item was removed. Refresh first.");
    // Manual editing submits only the six player fields. Preserve CSV metadata
    // from the live record when updating duplicate keys and production status.
    const current=previous.data(),next={...current,...item};
    const keys={...job.data().itemKeys},key=JSON.stringify(itemKey(next));
    if(keys[key] && keys[key]!==itemId)throw new Error("An item with this player, type, jersey and order number already exists.");
    delete keys[JSON.stringify(itemKey(previous.data()))];keys[key]=itemId;
    const designChanged=["kind","name","jersey","graduationYear","size","photoReference","quantity"].some(field=>next[field]!==current[field])
      || ["firstName","lastName"].some(field=>typeof current[field]==="string" && next[field]!==current[field]);
    const needsReview=designChanged && STAGES.indexOf(productionStage(job.data().stage))>=3;
    transaction.update(itemRef,{...item,updatedAt:serverTimestamp()});transaction.update(jobRef,{itemKeys:keys,...(needsReview ? {stage:"Designing",approvalId:""} : {}),updatedAt:serverTimestamp()});
  });
}
export async function deleteManualSenior(jobId,item,confirmedLegacy=false) {
  return runTransaction(db,async transaction=>{
    const jobRef=doc(db,"banner_jobs",jobId),reference=doc(db,"banner_jobs",jobId,"items",item.id);
    const [job,current]=await Promise.all([transaction.get(jobRef),transaction.get(reference)]);
    if(!job.exists() || job.data().deletedAt || !current.exists())throw new Error("Job or senior is no longer available. Refresh the job.");
    const row=current.data();
    if(row.kind!=="senior" || (row.entrySource!=="manual" && !(row.entrySource==null && confirmedLegacy)))throw new Error("Only manually entered seniors can be deleted here.");
    if(row.updatedAt?.toMillis()!==item.updatedAt?.toMillis())throw new Error("This senior changed. Review the latest row before deleting it.");
    const keys={...job.data().itemKeys};
    Object.keys(keys).forEach(key=>{if(keys[key]===item.id)delete keys[key];});
    const needsReview=STAGES.indexOf(productionStage(job.data().stage))>=3;
    transaction.delete(reference);
    transaction.update(jobRef,{itemKeys:keys,...(needsReview ? {stage:"Designing",approvalId:""} : {}),updatedAt:serverTimestamp()});
  });
}
export async function carryDesignPreview(job,sourceId,designId,uid) {
  const jobRef=doc(db,"banner_jobs",job.id),sourceRef=doc(db,"banner_jobs",sourceId),sourceDesignRef=doc(db,"banner_jobs",sourceId,"designs",designId);
  const [source,design]=await Promise.all([getDoc(sourceRef),getDoc(sourceDesignRef)]);
  if(!source.exists() || !design.exists())throw new Error("Previous job or preview is unavailable.");
  if(!previousDesignJobs(job,[{...source.data(),id:sourceId}]).length)throw new Error("Choose an earlier job for the same school and team/sport.");
  const preview=design.data();if(!preview.thumbnailPath)throw new Error("That revision has no thumbnail to carry over.");
  const revision=doc(collection(db,"banner_jobs",job.id,"designs"));
  const blob=await thumbnailBlob(preview.thumbnailPath);
  const thumbnailPath=await uploadThumbnail(job.id,revision.id,blob);
  await runTransaction(db,async transaction=>{
    const [current,latestSource,latestDesign]=await Promise.all([transaction.get(jobRef),transaction.get(sourceRef),transaction.get(sourceDesignRef)]);
    if(!current.exists() || !latestSource.exists() || !latestDesign.exists())throw new Error("Job or preview is no longer available.");
    if(current.data().updatedAt?.toMillis()!==job.updatedAt?.toMillis())throw new Error("Job changed while copying. Review the latest job and try again.");
    if(!previousDesignJobs({...current.data(),id:job.id},[{...latestSource.data(),id:sourceId}]).length || latestDesign.data().thumbnailPath!==preview.thumbnailPath)throw new Error("Previous job changed. Review the matching job and try again.");
    transaction.set(revision,{label:preview.label,folderPath:"",year:preview.year,historical:false,thumbnailPath,createdBy:uid,createdAt:serverTimestamp()});
    transaction.update(jobRef,{basis:"previous",templateNumber:"",previousJobId:sourceId,previousDesignId:designId,selectedDesignId:revision.id,approvalId:"",stage:"Designing",updatedAt:serverTimestamp()});
  });
}
export async function recordDesignApproval(job,date,stage="Approved") {
  validDate(date,true);if(date>today())throw new Error("Approval date cannot be in the future.");
  if(job.basis==="none" || (!job.selectedDesignId && !job.templateNumber && job.basis!=="previous"))throw new Error("Select a banner design before recording approval.");
  const reference=doc(collection(db,"banner_jobs",job.id,"approvals"));
  return runTransaction(db,async transaction=>{
    const jobRef=doc(db,"banner_jobs",job.id),snapshot=await transaction.get(jobRef);
    if(!snapshot.exists())throw new Error("Job no longer exists.");
    const latest=snapshot.data(),selection=designSelection(latest);
    if(latest.selectedDesignId!==job.selectedDesignId || Object.entries(designSelection(job)).some(([key,value])=>selection[key]!==value))throw new Error("The selected design changed. Refresh and review it before saving approval.");
    const approval={designId:latest.selectedDesignId || "",selection,date,createdAt:serverTimestamp()};
    requireStage(stage,latest,[approval]);
    transaction.set(reference,approval);
    transaction.update(jobRef,{approvalId:reference.id,stage,updatedAt:serverTimestamp()});
  });
}
export async function importAppointmentBannerInfo(jobId,appointmentId,expectedText,expectedNotes) {
  return runTransaction(db,async transaction=>{
    const reference=doc(db,"banner_jobs",jobId);
    const [job,source]=await Promise.all([transaction.get(reference),transaction.get(doc(db,"appointments",appointmentId))]);
    if(!job.exists() || job.data().deletedAt || job.data().appointmentId!==appointmentId)throw new Error("The linked appointment changed or this job is unavailable. Review the job and try again.");
    if(!source.exists())throw new Error("The linked appointment is unavailable.");
    if(getBannerInfo(source.data())!==expectedText || (job.data().notes || "")!==expectedNotes)throw new Error("Banner info or job notes changed. Review the latest preview before importing.");
    const imported=appointmentBannerNotes(expectedNotes,{...source.data(),id:appointmentId});
    if(!imported.alreadyImported)transaction.update(reference,{notes:imported.notes,updatedAt:serverTimestamp()});
  });
}
export async function saveJobNotes(jobId,notes,expectedNotes) {
  if(typeof notes!=="string" || notes.length>4000)throw new Error("Keep notes to 4,000 characters.");
  return runTransaction(db,async transaction=>{
    const reference=doc(db,"banner_jobs",jobId),snapshot=await transaction.get(reference);
    if(!snapshot.exists())throw new Error("Job no longer exists.");
    if((snapshot.data().notes || "")!==expectedNotes)throw new Error("These notes changed while you were editing. Cancel and reopen the notes to review the latest version.");
    transaction.update(reference,{notes:notes.trim(),updatedAt:serverTimestamp()});
  });
}
export async function uploadThumbnail(jobId,designId,file) {
  if(!["image/jpeg","image/png","image/webp"].includes(file.type) || file.size>2*1024*1024)throw new Error("Choose a JPG, PNG or WebP thumbnail up to 2 MB.");
  const path=`banner_previews/${jobId}/${designId}/thumbnail`;
  await uploadBytes(ref(getStorage(),path),file,{contentType:file.type});return path;
}
export async function thumbnailBlob(path) {
  const storage=getStorage();storage.maxOperationRetryTime=12000;
  let timer;
  try {
    // These objects are capped at 2 MB by Storage rules. Avoid a Range header,
    // which adds another preflight requirement on some NAS office networks.
    const blob=await Promise.race([getBlob(ref(storage,path)),new Promise((_,reject)=>{timer=setTimeout(()=>reject(Object.assign(new Error("Preview download timed out."),{code:"preview/timeout"})),15000);})]);
    if(blob.size>2*1024*1024)throw Object.assign(new Error("Preview exceeds the size limit."),{code:"preview/too-large"});
    return blob;
  }finally{clearTimeout(timer);}
}
